import { PageHeader } from '@components/PageHeader';
import { Card } from '@components/ui/Card';
import { Button } from '@components/ui/Button';
import { useToast } from '@hooks';
import { useEstateStore } from '../../stores/estateStore';
import { estateItemsToJson } from '../../utils/mergeEstates';

export const Export = () => {
  const { toast } = useToast();
  const { metadata, getLots, getReferences } = useEstateStore();

  const lots = getLots();
  const references = getReferences();

  const exportData = estateItemsToJson(metadata, [...lots, ...references]);

  const exportJson = JSON.stringify(exportData, null, 2);

  const handleCopy = () => {
    navigator.clipboard.writeText(exportJson);
    toast('JSON copied to clipboard', 'success');
  };

  const handleDownload = () => {
    const blob = new Blob([exportJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${metadata?.name?.replace(/\s+/g, '_') || 'estate'}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast('JSON file downloaded', 'success');
  };

  return (
    <div>
      <PageHeader
        title="Export"
        subtitle={`${metadata?.name || 'Estate'} • ${lots.length} lots • ${references.length} references`}
        showBackButton
        actions={
          <>
            <Button variant="outlined" onClick={handleCopy}>
              Copy to Clipboard
            </Button>
            <Button onClick={handleDownload}>Download JSON</Button>
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card>
            <h3 className="mb-4 text-xl font-bold text-gray-800">JSON Output</h3>
            <pre className="max-h-[600px] overflow-auto rounded-lg bg-gray-100 p-4 text-xs">
              {exportJson}
            </pre>
          </Card>
        </div>

        <Card>
          <h3 className="mb-4 text-xl font-bold text-gray-800">Summary</h3>
          <div className="space-y-3">
            <div>
              <p className="text-xs font-bold uppercase text-gray-700">Estate</p>
              <p className="text-s text-gray-600">{metadata?.name}</p>
            </div>
            <div>
              <p className="text-xs font-bold uppercase text-gray-700">Stages</p>
              {metadata?.stages?.length ? (
                <ul className="mt-1 space-y-1 text-xs text-gray-600">
                  {metadata.stages.map((stage, index) => (
                    <li key={`${stage.name}-${index}`}>
                      <span className="font-bold">{stage.name}</span> ·{' '}
                      {stage.release_status === 'future' ? 'Future' : 'Available'} ·{' '}
                      {(stage.lots ?? []).length} lot{(stage.lots ?? []).length === 1 ? '' : 's'}
                      {stage.show_price_publicly === false ? ' · prices hidden' : ''}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-s text-gray-600">None</p>
              )}
            </div>
            <div>
              <p className="text-xs font-bold uppercase text-gray-700">Lots</p>
              <p className="text-s text-gray-600">{lots.length}</p>
            </div>
            <div>
              <p className="text-xs font-bold uppercase text-gray-700">References</p>
              <p className="text-s text-gray-600">{references.length}</p>
              {references.length > 0 && (
                <ul className="mt-1 space-y-1 text-xs text-gray-500">
                  {references.map((ref) => (
                    <li key={ref.handleid}>
                      {ref.code} ({ref.classification})
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
